import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  leadApi,
  type Lead,
  type LeadFilters,
  type CreateLeadRequest,
  type TransitionStageRequest,
  type PaginatedResponse,
  type ApiError,
} from '../lib/api'

/**
 * Hook for fetching paginated leads list.
 */
export function useLeads(filters: LeadFilters = {}) {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: () => leadApi.list(filters),
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook for fetching a single lead by ID.
 */
export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadApi.get(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for creating a new lead.
 */
export function useCreateLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateLeadRequest) => leadApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Hook for updating an existing lead.
 */
export function useUpdateLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateLeadRequest> }) =>
      leadApi.update(id, data),
    onSuccess: (updatedLead) => {
      queryClient.setQueryData(['lead', updatedLead.id], updatedLead)
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Hook for deleting a lead.
 */
export function useDeleteLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => leadApi.delete(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: ['lead', id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Hook for transitioning a lead's pipeline stage.
 */
export function useTransitionLeadStage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TransitionStageRequest }) =>
      leadApi.transitionStage(id, data),
    onSuccess: (updatedLead) => {
      queryClient.setQueryData(['lead', updatedLead.id], updatedLead)
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline'] })
    },
  })
}

/**
 * Pipeline stages with labels and colors per CLAUDE.md Section 8.
 */
export const PIPELINE_STAGES = {
  new_business: {
    label: 'New Business',
    color: 'gray',
    subStages: ['needs_analysis', 'negotiation', 'pending_docs', 'application_entry'],
  },
  esign_sent: {
    label: 'eSign Sent',
    color: 'blue',
  },
  esign_completed: {
    label: 'eSign Completed',
    color: 'blue',
  },
  verification: {
    label: 'Verification',
    color: 'green',
  },
  processor_submission: {
    label: 'Processor Submission',
    color: 'purple',
  },
  underwriting_review: {
    label: 'Underwriting Review',
    color: 'purple',
  },
  conditional_approval: {
    label: 'Conditional Approval',
    color: 'amber',
  },
  pended: {
    label: 'Pended',
    color: 'amber',
  },
  client_returned: {
    label: 'Client Returned',
    color: 'red',
  },
  ready_to_build: {
    label: 'Ready to Build',
    color: 'teal',
  },
  activation: {
    label: 'Activation',
    color: 'teal',
  },
  live: {
    label: 'Live',
    color: 'emerald',
  },
  declined_by_agms: {
    label: 'Declined by AGMS',
    color: 'red',
  },
  declined_by_fiserv: {
    label: 'Declined by Fiserv',
    color: 'red',
  },
} as const

/**
 * Stages that can ONLY be reached by automated event sources
 * (BoldSign webhooks, Fiserv polling, deployment jobs). These are
 * NEVER shown in the "Change Pipeline Stage" dropdown.
 *
 * Everything else is fair game for manual transitions from any
 * stage. This covers real operational needs like internal pends
 * before Fiserv submission, equipment reorders on live accounts,
 * rewinds after declined esigns, etc.
 *
 * @see CLAUDE.md Section 8 — Lead pipeline configuration
 * @see Lead::AUTO_ONLY_STAGES (backend source of truth)
 */
export const AUTO_ONLY_STAGES = new Set([
  'esign_completed',      // BoldSign Completed webhook — only meaningful when BoldSign confirms
  'conditional_approval', // Fiserv polling — ops can't determine this status
  'client_returned',      // Fiserv polling — ops can't determine this status
  'live',                 // First batch detection — transaction sync only
])

/**
 * Get the list of stages a lead can be manually moved to from its
 * current stage. Filters out auto-only stages and the current stage.
 */
export function manualTransitionTargets(currentStage: string): string[] {
  return Object.keys(PIPELINE_STAGES).filter(
    (s) => s !== currentStage && !AUTO_ONLY_STAGES.has(s),
  )
}

/**
 * Lead sources with labels per CLAUDE.md Section 8.
 */
export const LEAD_SOURCES = {
  referral: 'Referral',
  inbound_call: 'Inbound Call',
  referral_partner: 'Referral Partner',
  event: 'Event',
  cold_outreach: 'Cold Outreach',
  social_media: 'Social Media',
  direct_marketing: 'Direct Marketing',
  website: 'Website',
  fb_messenger: 'Facebook Messenger',
  instagram: 'Instagram DM',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  web_chat: 'Website Chat',
} as const

/**
 * Ownership types.
 */
export const OWNERSHIP_TYPES = [
  'Corporation',
  'LLC',
  'Sole Proprietor',
  'Partnership',
  'Non-Profit',
] as const

export type {
  Lead,
  LeadFilters,
  CreateLeadRequest,
  TransitionStageRequest,
  PaginatedResponse,
  ApiError,
}
